import React, { useState, useEffect, useContext } from 'react';
import { Container, Card, Button, Modal, Form, Alert, Row, Col } from 'react-bootstrap';
import { FaPlus, FaEdit, FaTrash, FaBuilding, FaTimes, FaSave, FaSearch, FaFilter } from 'react-icons/fa';
import branchService from '../services/branchService';
import { AuthContext } from '../context/AuthContext';

const BranchList = () => {
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    city: '',
    address: '',
    phone: '',
    contact_person: ''
  });
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [cityFilter, setCityFilter] = useState('');
  const { user: currentUser } = useContext(AuthContext);

  useEffect(() => {
    fetchBranches();
  }, []);

  const fetchBranches = async () => {
    try {
      const data = await branchService.getAll();
      setBranches(data.branches || data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleShowModal = () => {
    setFormData({
      name: '',
      code: '',
      city: '',
      address: '',
      phone: '',
      contact_person: ''
    });
    setError('');
    setEditingId(null);
    setShowModal(true);
  };

  const handleShowEdit = (branch) => {
    setEditingId(branch.id);
    setFormData({
      name: branch.name || '',
      code: branch.code || '',
      city: branch.city || '',
      address: branch.address || '',
      phone: branch.phone || '',
      contact_person: branch.contact_person || ''
    });
    setError('');
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setError('');
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');

    try {
      if (editingId) {
        await branchService.update(editingId, formData);
      } else {
        await branchService.create(formData);
      }
      handleCloseModal();
      fetchBranches();
    } catch (err) {
      setError(err.response?.data?.error || 'فشل إنشاء الفرع');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('هل أنت متأكد من حذف هذا الفرع؟ لا يمكن التراجع عن هذا الإجراء.')) return;
    try {
      await branchService.delete(id);
      fetchBranches();
      alert('تم حذف الفرع');
    } catch (err) {
      console.error(err);
      const msg = err.response?.data?.error || 'فشل حذف الفرع';
      alert(msg);
    }
  };

  const cityOptions = [...new Set(branches.map((branch) => branch.city).filter(Boolean))].sort((a, b) => a.localeCompare(b, 'ar'));
  const filteredBranches = branches.filter((branch) => {
    const normalizedSearch = searchTerm.trim().toLowerCase();
    const matchesSearch = !normalizedSearch || [
      branch.name,
      branch.code,
      branch.city,
      branch.address,
      branch.phone,
      branch.contact_person
    ].filter(Boolean).some((value) => value.toLowerCase().includes(normalizedSearch));

    const matchesCity = !cityFilter || branch.city === cityFilter;
    return matchesSearch && matchesCity;
  });

  const hasActiveFilters = Boolean(searchTerm.trim() || cityFilter);

  if (loading) {
    return (
      <Container fluid className="page-container">
        <div className="dashboard-header-modern">
          <div className="header-content">
            <div className="header-icon">
              <FaBuilding />
            </div>
            <div>
              <h1 className="header-title">الفروع</h1>
              <p className="header-subtitle">إدارة فروع مصرف شمال أفريقيا</p>
            </div>
          </div>
        </div>
        <Card className="dashboard-card">
          <Card.Body className="p-4">
            <div className="skeleton skeleton-card"></div>
          </Card.Body>
        </Card>
      </Container>
    );
  }

  return (
    <Container fluid className="page-container branch-list-container">
      <div className="dashboard-header-modern">
        <div className="header-content">
          <div className="header-icon">
            <FaBuilding />
          </div>
          <div>
            <h1 className="header-title">الفروع</h1>
            <p className="header-subtitle">
              إدارة فروع مصرف شمال أفريقيا ({filteredBranches.length} من {branches.length} فرع)
            </p>
          </div>
        </div>
        <Button className="btn-action-create" onClick={handleShowModal} title="إنشاء فرع جديد">
          <FaPlus className="btn-icon" /> إضافة فرع
        </Button>
      </div>

      <Card className="dashboard-card management-filter-card mb-4">
        <Card.Body>
          <Row className="g-3 align-items-end">
            <Col md={8}>
              <Form.Group className="mb-0">
                <Form.Label className="filter-label">البحث في الفروع</Form.Label>
                <div className="search-wrapper">
                  <span className="search-icon"><FaSearch /></span>
                  <Form.Control
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="ابحث باسم الفرع، الكود، المدينة، أو مسؤول الاتصال"
                    className="search-input form-control-custom"
                  />
                </div>
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group className="mb-0">
                <Form.Label className="filter-label">تصفية حسب المدينة</Form.Label>
                <Form.Select
                  value={cityFilter}
                  onChange={(e) => setCityFilter(e.target.value)}
                  className="form-select-custom"
                >
                  <option value="">كل المدن</option>
                  {cityOptions.map((city) => (
                    <option key={city} value={city}>{city}</option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>

          <div className="management-filter-actions mt-3">
            {hasActiveFilters && (
              <Button
                variant="outline-secondary"
                className="btn-action-cancel"
                onClick={() => {
                  setSearchTerm('');
                  setCityFilter('');
                }}
              >
                <FaTimes className="btn-icon" /> مسح الفلاتر
              </Button>
            )}
            <span className="text-muted small d-inline-flex align-items-center gap-2">
              <FaFilter /> نتائج العرض: {filteredBranches.length}
            </span>
          </div>
        </Card.Body>
      </Card>

      <Card className="dashboard-card branch-table-card">
        <Card.Body className="p-0">
          {filteredBranches.length === 0 ? (
            <div className="table-empty-state">
              <div className="table-empty-state-icon">
                <FaBuilding size={48} />
              </div>
              <h5 className="empty-state-title">لا توجد فروع مطابقة</h5>
              <p className="empty-state-description">
                {hasActiveFilters ? 'لا توجد نتائج مطابقة لمعايير البحث الحالية' : 'لم يتم إضافة أي فروع بعد'}
              </p>
              {hasActiveFilters ? (
                <Button className="btn-action-cancel" onClick={() => { setSearchTerm(''); setCityFilter(''); }}>
                  <FaTimes className="btn-icon" /> مسح الفلاتر
                </Button>
              ) : (
                <Button className="btn-action-create" onClick={handleShowModal}>
                  <FaPlus className="btn-icon" /> إضافة أول فرع
                </Button>
              )}
            </div>
          ) : (
            <div className="table-responsive-wrapper responsive-table-wrapper">
              <table className="modern-table responsive-data-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>اسم الفرع</th>
                    <th>الكود</th>
                    <th>المدينة</th>
                    <th>العنوان</th>
                    <th>الهاتف</th>
                    <th>مسؤول الاتصال</th>
                    <th className="text-center">إجراءات</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredBranches.map((branch, index) => (
                    <tr key={branch.id}>
                      <td data-label="#"><span className="text-muted">{index + 1}</span></td>
                      <td data-label="اسم الفرع"><span className="fw-bold">{branch.name}</span></td>
                      <td data-label="الكود"><span className="cell-badge">{branch.code}</span></td>
                      <td data-label="المدينة">{branch.city || '-'}</td>
                      <td data-label="العنوان">{branch.address || '-'}</td>
                      <td data-label="الهاتف">{branch.phone || '-'}</td>
                      <td data-label="مسؤول الاتصال">{branch.contact_person || '-'}</td>
                      <td data-label="إجراءات" className="table-cell-actions">
                        <div className="table-actions">
                          <Button 
                            className="btn-action-edit" 
                            onClick={() => handleShowEdit(branch)}
                            title={`تعديل بيانات الفرع ${branch.name}`}
                          >
                            <FaEdit size={14} />
                            <span>تعديل</span>
                          </Button>
                          {currentUser?.role === 'admin' && (
                            <Button 
                              className="btn-action-delete" 
                              onClick={() => handleDelete(branch.id)}
                              title={`حذف الفرع ${branch.name}`}
                            >
                              <FaTrash size={14} />
                              <span>حذف</span>
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card.Body>
      </Card>

      <Modal show={showModal} onHide={handleCloseModal} size="lg" centered>
        <Modal.Header closeButton className="modal-header-modern">
          <Modal.Title>
            {editingId ? (
              <>
                <FaEdit className="ms-2" /> تعديل الفرع
              </>
            ) : (
              <>
                <FaPlus className="ms-2" /> إضافة فرع جديد
              </>
            )}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {error && <Alert variant="danger" className="alert-modern">{error}</Alert>}

          <Form onSubmit={handleSubmit}>
            <Row>
              <Col md={6}>
                <Form.Group className="form-field">
                  <Form.Label className="field-label">اسم الفرع *</Form.Label>
                  <Form.Control
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    placeholder="مثال: الفرع الرئيسي"
                    className="form-field-input"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="form-field">
                  <Form.Label className="field-label">الكود</Form.Label>
                  <Form.Control
                    type="text"
                    name="code"
                    value={formData.code}
                    onChange={handleChange}
                    placeholder="مثال: BR001"
                    className="form-field-input"
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="form-field">
                  <Form.Label className="field-label">المدينة</Form.Label>
                  <Form.Control
                    type="text"
                    name="city"
                    value={formData.city}
                    onChange={handleChange}
                    placeholder="مثال: طرابلس"
                    className="form-field-input"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="form-field">
                  <Form.Label className="field-label">الهاتف</Form.Label>
                  <Form.Control
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="مثال: 0211234567"
                    className="form-field-input"
                  />
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="form-field">
              <Form.Label className="field-label">العنوان</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                name="address"
                value={formData.address}
                onChange={handleChange}
                placeholder="العنوان الكامل للفرع"
                className="form-field-input"
              />
            </Form.Group>

            <Form.Group className="form-field">
              <Form.Label className="field-label">مسؤول الاتصال</Form.Label>
              <Form.Control
                type="text"
                name="contact_person"
                value={formData.contact_person}
                onChange={handleChange}
                placeholder="اسم مسؤول الفرع"
                className="form-field-input"
              />
            </Form.Group>

            <div className="form-actions">
              <Button variant="primary" className="btn-action-save" type="submit" disabled={submitting}>
                <FaSave className="btn-icon" />
                {submitting ? 'جاري الحفظ...' : 'حفظ'}
              </Button>
              <Button variant="secondary" className="btn-action-cancel" onClick={handleCloseModal} disabled={submitting}>
                <FaTimes className="btn-icon" />
                إلغاء
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>
    </Container>
  );
};

export default BranchList;
